<template>
    <div>
        <label class="label">{{ $t('modals.patient_profile.profile.request.email_head') }}</label>
        <div class="tabhead">
            <form-wrapper class="practice-information-form">
                <form-row>
                    <form-cell width="half">
                        <label class="form-field__label">
                            {{ $t('forms.patient_profile.fields.email.label') }}
                        </label>
                        <text-field
                            :placeholder="$t('forms.patient_profile.fields.email.insurance_plans.placeholder')"
                            :isRequired="true"
                            :formData="data"
                            valuePath="toProviderName"
                            @change="change($event)"
                        ></text-field>
                    </form-cell>
                    <form-cell width="half">
                        <label class="form-field__label">
                            {{ $t('forms.patient_profile.fields.email.document.label') }}
                        </label>
                        <text-field
                            :placeholder="$t('forms.patient_profile.fields.email.document.placeholder')"
                            :isRequired="true"
                            :formData="data"
                            valuePath="toProviderEmail"
                            @change="change($event)"
                        ></text-field>
                    </form-cell>
                </form-row>
                <form-row>
                    <form-cell width="half">
                        <label class="form-field__label">
                            {{ $t('forms.patient_profile.fields.email.provider.label') }}
                        </label>
                        <phone-field
                            :placeholder="$t('forms.patient_profile.fields.email.provider.placeholder')"
                            :isRequired="true"
                            :formData="data"
                            valuePath="toProviderPhone"
                            type="number"
                            @change="change($event)"
                        ></phone-field>
                    </form-cell>
                    <form-cell width="half">
                        <label class="form-field__label">
                            {{ $t('forms.patient_profile.fields.email.address.label') }}
                        </label>
                        <text-field
                            :placeholder="$t('forms.patient_profile.fields.email.address.placeholder')"
                            :isRequired="true"
                            :formData="data"
                            valuePath="toProviderAddress"
                            @change="change($event)"
                        ></text-field>
                    </form-cell>
                </form-row>
            </form-wrapper>
        </div>

        <!-- Records Needed -->
        <div class="section">
            <h3 class="hipa-title">{{ $t('modals.patient_profile.profile.request.record') }}</h3>
            <div class="checkbox-group">
                <label v-for="record in recordTypeSources" :key="record.value">
                    <input v-model="selectedRecordIds" type="checkbox" :value="record.value" />
                    <p>{{ record.label }}</p>
                </label>
            </div>
        </div>

        <!-- HIPAA Section -->
        <form-wrapper class="practice-information-form">
            <form-row>
                <form-cell width="full">
                    <label class="form-field__label">
                        {{ $t('forms.patient_profile.fields.hipaa.authorization.label') }}
                    </label>
                    <select-input
                        class="schedule-apply__input"
                        :placeholder="$t('forms.patient_profile.fields.hipaa.authorization.placeholder')"
                        :hasAutofocus="true"
                        :options="hipaaFormOptions"
                        :disabled="!hipaaFormOptions || hipaaFormOptions.length === 0"
                        :value="hipaaFormId"
                        @change="changeHipaa($event)"
                    ></select-input>
                    <p v-if="!hipaaFormOptions || hipaaFormOptions.length === 0" class="form-error-text">
                        {{ $t('modals.patient_profile.profile.request.hipaa.no_hipaa') }}
                    </p>
                </form-cell>
            </form-row>
            <h3 class="hipa-titles">{{ $t('modals.patient_profile.profile.request.range') }}</h3>
            <form-row>
                <form-cell width="half">
                    <label class="form-field__label">
                        {{ $t('forms.patient_profile.fields.date_range.label') }}
                    </label>
                    <text-field
                        class=""
                        :placeholder="$t('forms.patient_profile.fields.date_range.insurance_plans.placeholder')"
                        :isRequired="false"
                        :maxDate="maxSelectableDate"
                        :formData="data"
                        valuePath="dateFrom"
                        type="date"
                        @change="change($event)"
                    ></text-field>
                </form-cell>
                <form-cell width="half">
                    <label class="form-field__label">
                        {{ $t('forms.patient_profile.fields.date_range.document.label') }}
                    </label>
                    <text-field
                        class=""
                        :placeholder="$t('forms.patient_profile.fields.date_range.document.placeholder')"
                        :isRequired="false"
                        :maxDate="maxSelectableDate"
                        :formData="data"
                        valuePath="dateTo"
                        type="date"
                        @change="change($event)"
                    ></text-field>
                </form-cell>
            </form-row>
            <form-row>
                <form-cell width="full">
                    <label class="form-field__label">
                        {{ $t('forms.patient_profile.fields.date_range.provider.label') }}
                    </label>
                    <select-input
                        class="schedule-apply__input"
                        :placeholder="$t('forms.patient_profile.fields.date_range.provider.placeholder')"
                        :hasAutofocus="true"
                        :options="requestPurposeOption"
                        :value="requestPurposeId"
                        @change="changeRequestPurpose($event)"
                    ></select-input>
                </form-cell>
            </form-row>
            <form-row>
                <form-cell width="full">
                    <textarea-field
                        :label="$t('forms.patient_profile.fields.profile.additional.label')"
                        :placeholder="$t('forms.patient_profile.fields.profile.additional.placeholder')"
                        :isRequired="true"
                        :formData="data"
                        valuePath="notes"
                        @change="change($event)"
                    ></textarea-field>
                </form-cell>
            </form-row>
        </form-wrapper>

        <div class="submit">
            <button class="request" @click="submitEmailRecordRequest">
                <common-icon name="send" size="medium"></common-icon>
                <span>{{ $t('forms.patient_profile.fields.date_range.button.label') }}</span>
            </button>
        </div>
        <common-notification ref="commonSuccessNotificationChild" :content="notificationMessage"></common-notification>
    </div>
</template>

<script>
    import FormMixin from 'mixins/FormMixin'
    import HandleRequestMixin from 'mixins/HandleRequestMixin'
    import dayjs from 'dayjs'
    import { dateFormate } from '~/helpers'
    import PatientRecordsAPI from 'api/PatientRecordsAPI'

    export default {
        name: 'request-records-form',
        mixins: [FormMixin, HandleRequestMixin],

        props: {
            availHipaaForms: { type: Array, required: true },
            recordTypeSources: { type: Array, required: true },
        },

        data() {
            return {
                selectedDocument: null,
                locationOptions: [{ label: 'General HIPAA Authorization (expires 15/01/2026)', value: 1 }],
                requestPurposeOption: [
                    { label: 'Continuing Care', value: 1 },
                    { label: 'Specialist Referral', value: 2 },
                    { label: 'Second Opinion', value: 3 },
                    { label: 'Personal Records', value: 4 },
                    { label: 'Insurance Claim', value: 5 },
                    { label: 'Legal Proceeding', value: 6 },
                ],
                selectedRecordIds: [],
                notificationMessage: '',
                hipaaFormId: null,
                requestPurposeId: null,
            }
        },

        computed: {
            hipaaFormOptions() {
                const today = dayjs()

                return (this.availHipaaForms || [])
                    .filter((form) => !dayjs(form.expiryDate).isBefore(today, 'day'))
                    .map((form) => ({
                        label: `${form.authorizationName} - ${form.scope}  (expires ${dateFormate(form.expiryDate)})`,
                        value: form.id,
                    }))
            },
            selectedRecordTypes() {
                return this.recordTypeSources
                    .filter((record) => this.selectedRecordIds.includes(record.value))
                    .map((record) => record.label)
            },
            selectedPurpose() {
                return this.requestPurposeOption.find((item) => item.value === this.requestPurposeId)
            },
            maxSelectableDate() {
                return dayjs().format('YYYY-MM-DD')
            },
        },
        methods: {
            dateFormate,
            changeHipaa(event) {
                this.hipaaFormId = event
            },
            changeRequestPurpose(event) {
                this.requestPurposeId = event
            },
            submitEmailRecordRequest() {
                this.notificationMessage = ''
                const payload = {
                    patientId: Number(this.$route.params.id),
                    toProviderName: this.data.toProviderName,
                    toProviderEmail: this.data.toProviderEmail,
                    toProviderPhone: this.data.toProviderPhone,
                    toProviderAddress: this.data.toProviderAddress,
                    fromProviderId: this.$auth.getUser().id,
                    dateFrom: this.data.dateFrom,
                    dateTo: this.data.dateTo,
                    purpose: this.selectedPurpose?.label,
                    recordTypes: this.selectedRecordTypes,
                    notes: this.data.notes,
                    hipaaFormId: this.hipaaFormId,
                    emailRequest: true,
                }
                PatientRecordsAPI.create(payload).then(() => {
                    this.notificationMessage = this.$t('modals.patient_profile.profile.request.notification')
                    this.$refs.commonSuccessNotificationChild.showNotification()
                    this.resetRecordRequestEmailForm()
                    this.emitEvent({ name: 'refresh-record-request' })
                })
            },
            resetRecordRequestEmailForm() {
                this.data.dateFrom = null
                this.data.dateTo = null
                this.data.notes = ''
                this.data.toProviderName = null
                this.data.toProviderEmail = null
                this.data.toProviderPhone = null
                this.data.toProviderPhone = null
                this.hipaaFormId = null
                this.requestPurposeId = null
                this.selectedRecordIds = []
            },
        },
    }
</script>

<style scoped lang="scss">
    @import 'styles/setup/_variables.scss';
    @import 'styles/setup/_mixins.scss';

    .label {
        font-weight: 500;
        display: block;
        margin-bottom: 8px;
        color: $color-dark-2;
        font-size: 16px;
    }

    .tabhead {
        padding-bottom: 25px;
        border-bottom: 1px solid #ddd;
    }

    .section {
        margin-bottom: 20px;
    }

    .hipa-title,
    .hipa-titles {
        font-size: 12px;
        font-weight: 500;
        color: $color-dark-2;
        margin: 10px 0px;
    }

    .hipa-titles {
        margin-top: 18px;
    }

    .checkbox-group {
        display: grid;
        grid-template-columns: repeat(4, 1fr);
        gap: 8px 20px;
        font-size: 14px;
        align-items: center;
    }

    .checkbox-group label {
        display: flex;
    }

    .checkbox-group label p {
        margin-left: 10px;
        color: $color-dark-2;
        font-size: 13px;
        font-weight: 500;
    }

    .submit {
        margin-top: 20px;
    }

    .request {
        background-color: $color--accent-19;
        border-radius: 8px;
        color: #fff;
        padding: 8px 12px;
        margin: 10px 0px;

        display: inline-flex;
        align-items: center;
        gap: 14px;
    }
    .request :deep(svg),
    .request :deep(i) {
        color: #fff;
        fill: #fff;
    }
    .form-error-text {
        color: #e53935;
        font-size: 12px;
        margin-top: 6px;
    }
    @media screen and (max-width: 600px) {
        .checkbox-group {
            grid-template-columns: repeat(2, 1fr);
        }
    }
</style>
